// Tiny DOM helpers.

let uid = 0;
export function nextId(prefix = 'tool') {
  uid += 1;
  return `${prefix}-${uid}`;
}

/**
 * el('div', { class: 'x', onclick: fn, dataset: {a: 1}, attrs }, child, 'text', [more])
 * Props starting with "on" become listeners; booleans toggle attributes.
 */
export function el(tag, props = {}, ...children) {
  const node = document.createElement(tag);
  setProps(node, props);
  append(node, children);
  return node;
}

export function setProps(node, props) {
  for (const [k, v] of Object.entries(props || {})) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
    else if (k === 'text') node.textContent = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
    else if (k in node && typeof v !== 'string' && k !== 'list') node[k] = v;
    else node.setAttribute(k, v === true ? '' : String(v));
  }
}

export function append(node, children) {
  for (const c of children.flat(Infinity)) {
    if (c === null || c === undefined || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return node;
}

export function clear(node) {
  while (node.firstChild) node.removeChild(node.firstChild);
  return node;
}

export function replace(node, ...children) {
  clear(node);
  return append(node, children);
}

export function on(target, type, handler, opts) {
  target.addEventListener(type, handler, opts);
  return () => target.removeEventListener(type, handler, opts);
}

export function debounce(fn, ms = 150) {
  let t;
  const wrapped = (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
  wrapped.flush = (...args) => {
    clearTimeout(t);
    fn(...args);
  };
  return wrapped;
}

/** Polite screen reader announcement via a shared visually hidden live region. */
export function announce(text, root = document.body) {
  let region = root.querySelector(':scope > .tool-announce');
  if (!region) {
    region = el('div', { class: 'tool-announce visually-hidden', 'aria-live': 'polite', role: 'status' });
    root.append(region);
  }
  region.textContent = '';
  setTimeout(() => {
    region.textContent = text;
  }, 30);
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const ta = el('textarea', { style: { position: 'fixed', top: '-1000px' } });
    ta.value = text;
    document.body.append(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (err) {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}
